import React, { useEffect } from 'react';
import {
  StyleSheet, Text, TouchableOpacity, View,
} from 'react-native';

import { TimeSlotEligibility, TimeSlotType } from 'fitbeat.models/bookings';

import createSlotColors from './ZoneSlotColors';

import Theme from '../../theme';
import { getMomentFormat } from '../../utils';
import { defaultScale } from '../../utils/scaling';
import { slotDimensions } from './ZoneTimeSlots';

function Slot({
  t,
  slot,
  index,
  isLast,
  isSelected,
  setPendingSlot,
  setTimeSlotType,
  scrollToTimeSlot,
  setTimeSlotEligibility,
  gymTimezone,
  isPreferred,
  areaCategory,
}) {
  useEffect(() => {
    if (slot.booked) {
      scrollToTimeSlot(index, false);
    } else if (isPreferred) {
      scrollToTimeSlot(index, false);
    }
  }, []);

  const isEligibleForSlot = slot.eligibility === TimeSlotEligibility.Eligible;
  const upgradeRequiredToEnableSlot = slot.eligibility === TimeSlotEligibility.SubscriptionUpgradeRequired;

  const getSlotDisplayInfo = () => {
    const {
      booked,
      isUnavailable,
      inWaitinglist,
      timeSlotType,
      isFull,
      startTime,
    } = slot;

    interface IAppliedStyle {
      backgroundColor?: string;
      color?: string;
      borderColor?: string;
      display?: 'none' | 'flex';
    }

    const SlotColors = createSlotColors(Theme.PRIMARY);
    let slotUserStateText = isFull && !isUnavailable ? t('waitlist') : '';
    let slotTimeText = getMomentFormat(startTime, 'HH:mm', gymTimezone);
    let appliedStyle: IAppliedStyle = SlotColors.AVAILABLE;

    // Todo: calculate the slotUserStateText in the backend and pass with slot.subText
    if (isUnavailable) {
      appliedStyle = { display: 'none' };
    } else if (booked) {
      slotUserStateText = t('booked');
      appliedStyle = SlotColors.CURRENT_BOOKING;
    } else if (inWaitinglist) {
      slotUserStateText = t('joinedWaitlist');
      appliedStyle = SlotColors.WAITING_LIST;
    } else if (isSelected) {
      appliedStyle = SlotColors.SELECTED;
    } else if (timeSlotType === TimeSlotType.None) {
      slotTimeText = t('unavailableSlot');
      appliedStyle = SlotColors.NOT_TYPESLOT_USER;
    } else if (!isEligibleForSlot && !upgradeRequiredToEnableSlot) {
      appliedStyle = SlotColors.NOT_TYPESLOT_USER;
    }

    const customMarginBottom = isLast ? 0 : 2;

    return {
      height: defaultScale(50),
      timeText: slotTimeText,
      stateText: slotUserStateText,
      slotStyle: appliedStyle,
      marginBottom: defaultScale(customMarginBottom),
    };
  };

  const onPress = () => {
    scrollToTimeSlot(index, slot.workoutType, true);
    setTimeSlotType(slot.timeSlotType);

    if (isEligibleForSlot || upgradeRequiredToEnableSlot) {
      setTimeSlotEligibility(slot.eligibility);
      setPendingSlot(slot, areaCategory);
    } else {
      setPendingSlot(null, undefined);
      setTimeSlotEligibility(slot.eligibility);
    }
  };

  const {
    height,
    timeText,
    stateText,
    marginBottom,
    slotStyle,
  } = getSlotDisplayInfo();

  const {
    borderColor, backgroundColor, color, display,
  } = slotStyle;

  const slotSubText = stateText || t(slot.subText);
  return (
    <View style={{
      height: defaultScale(50),
      marginRight: isLast ? defaultScale(40) : slotDimensions.marginRight,
      display: display || 'flex',
    }}
    >
      <TouchableOpacity
        style={[styles.container, {
          width: slotDimensions.width,
          height,
          borderColor,
          marginBottom,
          backgroundColor,
        }]}
        disabled={slot.booked || slot.isUnavailable}
        onPress={onPress}
      >
        <View style={styles.textContainer}>
          {!!timeText && (
            <Text style={[styles.slotTimeText, { color }]}>
              {timeText}
            </Text>
          )}

          {(!!slotSubText) && (
            <Text style={[styles.slotUserStateText, { color }]}>
              {slotSubText}
            </Text>
          )}
        </View>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderWidth: defaultScale(1),
    borderRadius: defaultScale(6),
    alignItems: 'center',
  },
  textContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  slotTimeText: {
    ...Theme.textStyle.bodyCopyReg,
    textAlign: 'center',
  },
  slotUserStateText: {
    ...Theme.textStyle.small,
    textAlign: 'center',
  },
});

export default Slot;
